import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { SuiModal, SuiModalContent } from '@splashthat/sui';
import styled from 'styled-components';

import SaveModalFooter from 'common/components/SaveModalFooter';
import { getFormLibraryFormId } from 'common/entities/form/selectors';

import { getEventCount, getFormLibraryStatus, getThemeCount, getFormLibraryError } from '../../selectors/formLibrary';
import { SAVED } from '../../reducers/formLibrary';
import { getEventThemeCount, saveFormLibraryName } from '../../epics/formLibraryEffects';
import { SAVE_RETROACTIVELY } from './ScaledFormSaveContent';
import { saveFormAsCopy, setFormLibraryError } from '../../actions/formLibrary';
import ScaledFormsModalContent from './ScaledFormsModalContent';
import ScaledFormsModalHeader from './ScaledFormsModalHeader';

const COMPONENT_NAME = 'ScaledFormSaveModal';

export const LIST_THEMES_EVENTS = 'themesEventsList';
export const SAVE_AS_COPY_CONFIRMATION = 'saveAsCopyConfirmation';
export const UPDATE_EXISITING_CONFIRMATION = 'updateExistingConfirmation';

export const SAVE_FORM_COMPONENT = 'saveOptionsComponent';
export const SAVE_FORM_TITLE = 'Save options';

export const UPDATE_EXISTING_TITLE = 'Confirm template update';
export const SAVE_AS_COPY_TITLE = 'Confirm changes on this event';
export const FORM_USAGE_TITLE = 'Form template usage';
export const GO_BACK_BUTTON_LABEL = 'Go Back';
export const MODAL_CANCEL_BUTTON_LABEL = 'Cancel';

const headlineTitles = {
    [SAVE_AS_COPY_CONFIRMATION]: SAVE_AS_COPY_TITLE,
    [UPDATE_EXISITING_CONFIRMATION]: UPDATE_EXISTING_TITLE,
    [SAVE_FORM_COMPONENT]: SAVE_FORM_TITLE,
    [LIST_THEMES_EVENTS]: FORM_USAGE_TITLE,
};

export const SAVE_LABEL = 'Save';
const CONFIRM_LABEL = 'Confirm';

export const StyledModalContent = styled(SuiModalContent)`
    height: 50vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-x: hidden;
    min-height: 380px;
`;

const ScaledFormSaveModal = (props) => {
    const [name, setName] = useState(props.formName);
    const [shownModalContent, setShownModalContent] = useState(SAVE_FORM_COMPONENT);
    const [saveMethod, setSaveMethod] = useState(SAVE_RETROACTIVELY);

    const error = useSelector(getFormLibraryError);

    const dispatch = useDispatch();
    const status = useSelector(getFormLibraryStatus);
    const eventCount = useSelector(getEventCount);
    const themeCount = useSelector(getThemeCount);
    const formLibraryFormId = useSelector((state) => getFormLibraryFormId(state, props.formId));

    useEffect(() => {
        if (status === SAVED) {
            props.onClose();
        }
    }, [status]);

    useEffect(() => {
        setName(props.formName);
    }, [props.formName]);

    useEffect(() => {
        error && setShownModalContent(SAVE_FORM_COMPONENT);
    }, [error]);

    useEffect(() => {
        if (props.open && !eventCount && !themeCount) {
            dispatch(getEventThemeCount(formLibraryFormId));
        }

        return () => {
            setShownModalContent(SAVE_FORM_COMPONENT);
            setSaveMethod(SAVE_RETROACTIVELY);
            error && dispatch(setFormLibraryError(false));
        };
    }, [props.open]);

    const getOnConfirm = {
        [SAVE_FORM_COMPONENT]: () =>
            setShownModalContent(
                saveMethod === SAVE_RETROACTIVELY ? UPDATE_EXISITING_CONFIRMATION : SAVE_AS_COPY_CONFIRMATION,
            ),
        [SAVE_AS_COPY_CONFIRMATION]: () => dispatch(saveFormAsCopy(props.formId)),
        [UPDATE_EXISITING_CONFIRMATION]: () => dispatch(saveFormLibraryName({ formId: props.formId, formName: name })),
    };

    return (
        <SuiModal
            open={props.open}
            size={SuiModal.THIN}
            useClickOutside
            onRequestClose={props.onClose}
            showCloseButton={false}
            data-qa={`${COMPONENT_NAME}__modal`}
        >
            <StyledModalContent variation="border standard flex-vertical" data-qa={`${COMPONENT_NAME}__modal_content`}>
                <ScaledFormsModalHeader headlineTitle={headlineTitles[shownModalContent]} />
                <ScaledFormsModalContent
                    shownModalContent={shownModalContent}
                    setShownModalContent={setShownModalContent}
                    themeCount={themeCount}
                    eventCount={eventCount}
                    formName={name}
                    onChangeTitle={(value) => setName(value)}
                    setSaveMethod={setSaveMethod}
                    saveMethod={saveMethod}
                    savedFormName={props.formName}
                    formId={props.formId}
                />
                <SaveModalFooter
                    onCancel={
                        shownModalContent === SAVE_FORM_COMPONENT
                            ? props.onClose
                            : () => setShownModalContent(SAVE_FORM_COMPONENT)
                    }
                    onConfirm={shownModalContent !== LIST_THEMES_EVENTS ? getOnConfirm[shownModalContent] : null}
                    confirmButtonLabel={shownModalContent === SAVE_FORM_COMPONENT ? SAVE_LABEL : CONFIRM_LABEL}
                    cancelButtonLabel={
                        shownModalContent === SAVE_FORM_COMPONENT ? MODAL_CANCEL_BUTTON_LABEL : GO_BACK_BUTTON_LABEL
                    }
                    status={status}
                    disabledConfirmButton={name.trim().length === 0}
                />
            </StyledModalContent>
        </SuiModal>
    );
};

ScaledFormSaveModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    formId: PropTypes.string.isRequired,
    formName: PropTypes.string,
};

ScaledFormSaveModal.defaultProps = {
    formName: '',
};

export default ScaledFormSaveModal;
